import jsPDF from 'jspdf';

/** Daten für die Coach-Honorarrechnung (jsPDF, ohne html2canvas). */
export interface CoachHonorariumPdfInput {
  coach?: Record<string, unknown> | null;
  invoiceNumber?: string;
  issueDate?: string;
  targetMonthText?: string;
  revenueNet?: number;
  managementBonus?: number;
  clearingTotal?: number;
  supervisedClientCount?: number;
  pepulseMalus?: number;
  pepulseMalusRate?: number;
  pepulsePoints?: number;
  pepulseMalusBase?: number;
  pepulseTargetPoints?: number;
  pepulseVacationDays?: number;
  pepulsePrevMonth?: string;
  pepulseRedistributionBonus?: number;
}

const n = (v: unknown) => Number(v) || 0;

const euro = (val: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n(val));

const points = (val: number) => new Intl.NumberFormat('de-DE').format(Math.round(n(val)));

const str = (v: unknown, fallback = '---') => {
  const s = String(v ?? '').trim();
  return s || fallback;
};

async function tryLoadLogoDataUrl(fileName: string): Promise<string | null> {
  const file = String(fileName || '').trim();
  if (!file) return null;
  const src =
    file.startsWith('http') || file.startsWith('data:') || file.startsWith('/')
      ? file
      : `/assets/rechnung/${file}`;
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Honorarrechnung als Vektor-PDF — html2canvas verdoppelt auf iOS den Text.
 */
export async function buildCoachHonorariumPdfDoc(input: CoachHonorariumPdfInput): Promise<jsPDF> {
  const coach = (input.coach || {}) as Record<string, unknown>;
  const reverseCharge = coach.invoiceReverseCharge === true;
  const vatLabel = reverseCharge ? '0%' : '19%';

  const revenueNet = n(input.revenueNet);
  const managementBonus = n(input.managementBonus);
  const clearingTotal = n(input.clearingTotal);
  const pepulseMalus = n(input.pepulseMalus);
  const pepulseRedistrib = n(input.pepulseRedistributionBonus);
  const pepulseMalusRate = n(input.pepulseMalusRate);

  const totalNet = revenueNet + managementBonus + clearingTotal + pepulseMalus + pepulseRedistrib;
  const taxRate = reverseCharge ? 0 : 0.19;
  const vatAmount = totalNet * taxRate;
  const totalGross = totalNet + vatAmount;

  const firmName = str(coach.invoiceCompanyName || coach.name, 'Coach');
  const showPepulseMalus = pepulseMalus < 0 && pepulseMalusRate > 0;
  const showPepulseRedistrib = pepulseRedistrib > 0;
  const vacDays = n(input.pepulseVacationDays);
  const targetPts = n(input.pepulseTargetPoints) || 1000;

  const lines: { title: string; sub: string; amount: number; amountColor?: [number, number, number] }[] = [];
  if (revenueNet !== 0) {
    const hasCount =
      typeof input.supervisedClientCount === 'number' && !Number.isNaN(input.supervisedClientCount);
    const countSuffix = hasCount
      ? ` · ${input.supervisedClientCount} ${input.supervisedClientCount === 1 ? 'Mandant' : 'Mandanten'} betreut`
      : '';
    lines.push({
      title: 'PreisExpert Abrechnung',
      sub: `Anteil an Einnahmen (Provision & Fixgehalt${countSuffix})`,
      amount: revenueNet,
    });
  }
  if (managementBonus > 0) {
    lines.push({
      title: 'Team-Bonus (Management)',
      sub: 'Anteilige Umsatzbeteiligung (Team-Split)',
      amount: managementBonus,
    });
  }
  if (clearingTotal !== 0) {
    lines.push({
      title: 'Clearing & Verrechnung',
      sub: 'zuzügl. Provisionsabrechnung / abzügl. Kosten',
      amount: clearingTotal,
    });
  }
  if (showPepulseMalus) {
    lines.push({
      title: 'PEPulse Social Media Malus',
      sub: `Vormonat ${str(input.pepulsePrevMonth, '—')} (${points(n(input.pepulsePoints))} / ${points(targetPts)} Pkt${vacDays > 0 ? `, ${vacDays} Urlaubstage` : ''} = ${pepulseMalusRate}% Abzug von ${euro(n(input.pepulseMalusBase))})`,
      amount: pepulseMalus,
      amountColor: [185, 28, 28],
    });
  }
  if (showPepulseRedistrib) {
    lines.push({
      title: 'PEPulse Malus-Umverteilung (50 %)',
      sub: `Anteil am Team-Malus-Pool Vormonat ${str(input.pepulsePrevMonth, '—')} (GF Christian / Martina)`,
      amount: pepulseRedistrib,
      amountColor: [4, 120, 87],
    });
  }
  if (lines.length === 0) {
    lines.push({
      title: 'Historischer Beleg',
      sub: 'Keine detaillierten Beträge hinterlegt',
      amount: 0,
    });
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 18;
  const right = pageW - margin;
  let y = 18;

  const logoData = await tryLoadLogoDataUrl(String(coach.invoiceLogoUrl || ''));
  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', margin, y, 42, 16);
    } catch {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.text('RECHNUNG', margin, y + 8);
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text('RECHNUNG', margin, y + 8);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(100, 116, 139);
  doc.text('HONORARABRECHNUNG / SERVICES', margin, y + 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text('Rechnung an:', margin, y + 30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text('PreisExpert', margin, y + 36);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.text('Christian Funk', margin, y + 41);
  doc.text('Rotehahnstr. 2', margin, y + 46);
  doc.text('21335 Lüneburg', margin, y + 51);
  doc.text('Deutschland', margin, y + 56);

  const boxX = 118;
  const boxW = right - boxX;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(boxX, y, boxW, 52, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  const firmLines = doc.splitTextToSize(firmName, boxW - 8);
  doc.text(firmLines, boxX + 4, y + 7);
  let by = y + 7 + firmLines.length * 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(71, 85, 105);
  doc.text(str(coach.invoiceStreet), boxX + 4, by);
  by += 4.2;
  doc.text(str(coach.invoiceCity), boxX + 4, by);
  by += 5;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(str(coach.email), boxX + 4, by);
  by += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Steuernr./USt:', boxX + 4, by);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(str(coach.invoiceTaxId), boxX + 28, by);
  by += 4.2;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('IBAN:', boxX + 4, by);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(8.5);
  doc.text(str(coach.invoiceIban), boxX + 18, by);
  by += 4.2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('BIC:', boxX + 4, by);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(str(coach.invoiceBic), boxX + 18, by);

  y += 64;
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.6);
  doc.line(margin, y, right, y);
  y += 8;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, right - margin, 16, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(148, 163, 184);
  doc.text('DATUM', margin + 4, y + 5);
  doc.text('ABRECHNUNGSMONAT', margin + 58, y + 5);
  doc.text('RECHNUNGS-NR.', right - 4, y + 5, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text(str(input.issueDate), margin + 4, y + 12);
  doc.text(str(input.targetMonthText, 'UNBEKANNT'), margin + 58, y + 12);
  doc.text(str(input.invoiceNumber), right - 4, y + 12, { align: 'right' });
  y += 24;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text('BESCHREIBUNG / LEISTUNG', margin, y);
  doc.text('MWST.', 138, y);
  doc.text('BETRAG (NETTO)', right, y, { align: 'right' });
  y += 2;
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.5);
  doc.line(margin, y, right, y);
  y += 8;

  lines.forEach((row) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(row.title, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(100, 116, 139);
    const subLines = doc.splitTextToSize(row.sub, 110);
    doc.text(subLines, margin, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(vatLabel, 145, y, { align: 'center' });
    const [r, g, b] = row.amountColor || [15, 23, 42];
    doc.setTextColor(r, g, b);
    doc.setFont('helvetica', 'bold');
    doc.text(euro(row.amount), right, y, { align: 'right' });
    y += 6 + subLines.length * 4 + 4;
    doc.setDrawColor(241, 245, 249);
    doc.setLineWidth(0.3);
    doc.line(margin, y - 2, right, y - 2);
  });

  y += 8;
  const sumX = 118;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Summe (Netto):', sumX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(euro(totalNet), right, y, { align: 'right' });
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(reverseCharge ? 'Umsatzsteuer (0%):' : 'Umsatzsteuer (19%):', sumX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(euro(vatAmount), right, y, { align: 'right' });
  y += 4;
  doc.setDrawColor(226, 232, 240);
  doc.line(sumX, y, right, y);
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('Gesamtsumme:', sumX, y);
  doc.setFillColor(126, 217, 87);
  const grossLabel = euro(totalGross);
  const boxWidth = Math.max(32, doc.getTextWidth(grossLabel) + 6);
  doc.rect(right - boxWidth, y - 5, boxWidth, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text(grossLabel, right - 2, y, { align: 'right' });
  y += 14;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, right - margin, 28, 1.5, 1.5, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  doc.text('ZAHLUNGSINFORMATIONEN', margin + 4, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(51, 65, 85);
  doc.text('Bitte überweisen Sie den gesamten Betrag schnellstmöglich auf folgendes Konto:', margin + 4, y + 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text(`IBAN: ${str(coach.invoiceIban)}`, margin + 4, y + 19);
  doc.text(`BIC:  ${str(coach.invoiceBic)}`, margin + 4, y + 24);
  y += 36;

  if (reverseCharge) {
    doc.setFillColor(255, 251, 235);
    doc.rect(margin, y - 4, right - margin, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(180, 83, 9);
    const rc = doc.splitTextToSize(
      'Steuerfreie innergemeinschaftliche Leistung / Steuerschuldner ist der Leistungsempfänger (Reverse Charge).',
      right - margin - 4
    );
    doc.text(rc, margin + 2, y);
    y += 12;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text('Vielen Dank für Ihren Auftrag!', margin, y);

  return doc;
}
